import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  DeleteOutline,
  EditOutlined,
  FileCopyOutlined,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import ConfirmDialog from "../../components/ConfirmDialog";
import ListPagination from "../../containers/ListPagination";
import QualifyingScoreFilters from "../../containers/QualifyingScoreFilters";
import QualifyingScoreForm from "../../containers/QualifyingScoreForm";
import QualifyingScoreCloneDialog from "../../containers/QualifyingScoreCloneDialog";
import QualifyingScoreBulkTemplateDialog from "../../containers/QualifyingScoreBulkTemplateDialog";

import {
  defaultParamsQualifyingScoresTable,
  QUALIFYING_SCORE_DEFAULT_SORT,
  TEXT_MATCH,
} from "../../constants/data";
import { PAGES_TITLES, TITLES } from "../../constants/titles";
import { GET_GOVERNING_BODIES_ADMIN_ALL } from "../../constants/endpoints";
import { getList } from "../../dataProvider/API";
import { normalizeGoverningBodiesPayload } from "../../utils/governingBodyApi";

import {
  cloneQualifyingScores,
  deleteQualifyingScore,
  downloadQualifyingScoresExport,
  fetchAllMatchingQualifyingScores,
  fetchQualifyingScoreMessageTemplates,
  fetchQualifyingScoreFilterValues,
  fetchQualifyingScores,
  saveQualifyingScore,
} from "./sliceReducer";
import {
  formatDateRange,
  formatFullDateTime,
  formatPoints,
  formatShortDateTime,
  heightLabel,
} from "../../utils/qualifyingScoreUtils";

const useStyles = makeStyles({
  page: {
    backgroundColor: "#f7f9fc",
    minHeight: "calc(100vh - 64px)",
    paddingTop: "24px",
    paddingBottom: "24px",
  },
  header: { marginBottom: "12px" },
  title: { fontSize: "28px", fontWeight: 700, color: "#101828" },
  subtitle: { marginTop: "4px", fontSize: "14px", color: "#475467" },
  tableWrapper: { marginTop: "8px" },
  cell: { padding: "8px", fontSize: "13px", whiteSpace: "nowrap" },
  headerCell: {
    padding: "8px",
    fontSize: "12px",
    fontWeight: 700,
    backgroundColor: "#f2f5fa",
  },
  rowEven: { backgroundColor: "#ffffff" },
  rowOdd: { backgroundColor: "#f8fafc" },
  // Actions stay visible at the right edge when the wide table scrolls sideways.
  stickyAction: {
    position: "sticky",
    right: 0,
    zIndex: 1,
    backgroundColor: "inherit",
    boxShadow: "inset 1px 0 0 #e4e7ec",
    padding: "0 4px",
    whiteSpace: "nowrap",
  },
  stickyActionHeader: {
    position: "sticky",
    right: 0,
    zIndex: 3,
    boxShadow: "inset 1px 0 0 #e4e7ec",
  },
  iconButton: { width: "44px", height: "44px" },
  deleteIcon: { color: "#b42318" },
  selectionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#eef4ff",
    border: "1px solid #b2ccf8",
    borderRadius: "6px",
    padding: "8px 12px",
    marginBottom: "8px",
  },
  emptyState: {
    textAlign: "center",
    padding: "32px 12px",
    color: "#667085",
  },
  progressWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "32px 0",
  },
  deleteSummary: {
    marginBottom: "12px",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "16px",
  },
});

const dash = "—";

const parseSortFromQuery = (search) => {
  const sp = new URLSearchParams(search);
  const sortRaw = sp.get("sort");
  const ascRaw = sp.get("isAscending");
  const sortTokens = sortRaw
    ? sortRaw.split(",").map((s) => s.trim()).filter(Boolean)
    : [...QUALIFYING_SCORE_DEFAULT_SORT];
  return {
    sortTokens,
    isAscending: ascRaw == null ? true : ascRaw !== "false",
  };
};

const matchFromQuery = (value) =>
  value === TEXT_MATCH.CONTAINS ? TEXT_MATCH.CONTAINS : TEXT_MATCH.EXACT;

// Sent only with a value, and only when it is not the backend's default (EXACT).
const matchParam = (value, match) =>
  value && match === TEXT_MATCH.CONTAINS ? TEXT_MATCH.CONTAINS : null;

const parseFiltersFromQuery = (search) => {
  const sp = new URLSearchParams(search);
  return {
    asOfDate: sp.get("asOfDate") || "",
    gender: sp.get("gender") || "",
    eventHeight: sp.get("eventHeight") ? Number(sp.get("eventHeight")) : null,
    ageGroup: sp.get("ageGroup") || "",
    divisionId: sp.get("divisionId") || "",
    regionId: sp.get("regionId") || "",
    divisionMatch: matchFromQuery(sp.get("divisionMatch")),
    regionMatch: matchFromQuery(sp.get("regionMatch")),
  };
};

const parsePageFromQuery = (search, defaultLimit) => {
  const sp = new URLSearchParams(search);
  return {
    page: sp.get("page") ? Number(sp.get("page")) : 0,
    limit: sp.get("size") ? Number(sp.get("size")) : defaultLimit,
  };
};

const buildQueryString = (state) => {
  const sp = new URLSearchParams();
  if (state.governingBodyId != null) sp.set("governingBodyId", String(state.governingBodyId));
  if (state.asOfDate) sp.set("asOfDate", state.asOfDate);
  if (state.gender) sp.set("gender", state.gender);
  if (state.eventHeight) sp.set("eventHeight", String(state.eventHeight));
  if (state.ageGroup) sp.set("ageGroup", state.ageGroup);
  if (state.divisionId) sp.set("divisionId", state.divisionId);
  if (matchParam(state.divisionId, state.divisionMatch)) {
    sp.set("divisionMatch", TEXT_MATCH.CONTAINS);
  }
  if (state.regionId) sp.set("regionId", state.regionId);
  if (matchParam(state.regionId, state.regionMatch)) {
    sp.set("regionMatch", TEXT_MATCH.CONTAINS);
  }
  if (state.sortTokens?.length) sp.set("sort", state.sortTokens.join(","));
  sp.set("isAscending", String(Boolean(state.isAscending)));
  if (state.page) sp.set("page", String(state.page));
  if (state.limit && state.limit !== defaultParamsQualifyingScoresTable.limit) {
    sp.set("size", String(state.limit));
  }
  return sp.toString();
};

const QualifyingScores = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { loading, saving, list, messageTemplates, filterValues } = useSelector(
    (state) => state.qualifyingScores,
  );

  const [governingBodies, setGoverningBodies] = useState([]);
  const [governingBodiesLoading, setGoverningBodiesLoading] = useState(false);

  const initialQueryGb = new URLSearchParams(location.search).get("governingBodyId");
  const [governingBodyId, setGoverningBodyId] = useState(
    initialQueryGb ? Number(initialQueryGb) : null,
  );
  const [filters, setFilters] = useState(() => parseFiltersFromQuery(location.search));
  const [pageState, setPageState] = useState(() =>
    parsePageFromQuery(location.search, defaultParamsQualifyingScoresTable.limit),
  );
  const [{ sortTokens, isAscending }, setSortState] = useState(() =>
    parseSortFromQuery(location.search),
  );

  const [selectedIds, setSelectedIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editDto, setEditDto] = useState(null);
  const [copyDto, setCopyDto] = useState(null);
  const [deleteDto, setDeleteDto] = useState(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  // Every row seen on any page (or via "select all matching"), so a selection spanning pages
  // still has each row's details.
  const [knownRows, setKnownRows] = useState({});

  const skipNextUrlSync = useRef(false);

  // Load list of governing bodies once. Use the admin endpoint so we get the
  // localized name (the public `governingbody/all` returns abbreviations only).
  useEffect(() => {
    let cancelled = false;
    setGoverningBodiesLoading(true);
    getList(
      GET_GOVERNING_BODIES_ADMIN_ALL({
        page: 0,
        limit: 500,
        sort: "Name",
        isAscending: true,
      }),
    )
      .then((payload) => {
        if (cancelled) return;
        const { rows } = normalizeGoverningBodiesPayload(payload, "en-US");
        setGoverningBodies(rows);
      })
      .catch(() => {
        if (!cancelled) setGoverningBodies([]);
      })
      .finally(() => {
        if (!cancelled) setGoverningBodiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Division/Region suggestions for the chosen governing body.
  useEffect(() => {
    if (!governingBodyId) return;
    dispatch(fetchQualifyingScoreFilterValues({ governingBodyId }));
  }, [dispatch, governingBodyId]);

  // Templates this governing body may use, loaded when the form opens so an edited score's
  // current (possibly deactivated) template is included.
  const editTemplateId = editDto?.messageTemplateId ?? null;
  useEffect(() => {
    if (!modalOpen || !governingBodyId) return;
    dispatch(
      fetchQualifyingScoreMessageTemplates({
        governingBodyId,
        includeId: editTemplateId,
      }),
    );
  }, [dispatch, modalOpen, governingBodyId, editTemplateId]);

  useEffect(() => {
    if (!bulkOpen || !governingBodyId) return;
    dispatch(fetchQualifyingScoreMessageTemplates({ governingBodyId }));
  }, [dispatch, bulkOpen, governingBodyId]);

  const rememberRows = useCallback((rows) => {
    if (!rows?.length) return;
    setKnownRows((prev) => {
      const next = { ...prev };
      rows.forEach((r) => {
        next[r.id] = r;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    rememberRows(list.rows);
  }, [list.rows, rememberRows]);

  const apiParams = useMemo(
    () => ({
      governingBodyId,
      asOfDate: filters.asOfDate || null,
      ageGroup: filters.ageGroup || null,
      gender: filters.gender || null,
      eventHeight: filters.eventHeight || null,
      divisionId: filters.divisionId || null,
      regionId: filters.regionId || null,
      divisionMatch: matchParam(filters.divisionId, filters.divisionMatch),
      regionMatch: matchParam(filters.regionId, filters.regionMatch),
      sort: sortTokens.join(","),
      isAscending,
      page: pageState.page,
      size: pageState.limit,
    }),
    [governingBodyId, filters, sortTokens, isAscending, pageState],
  );

  const reloadList = useCallback(() => {
    if (!governingBodyId) return;
    dispatch(fetchQualifyingScores({ params: apiParams }));
  }, [dispatch, governingBodyId, apiParams]);

  useEffect(() => {
    reloadList();
  }, [reloadList]);

  // Sync state to URL whenever it changes.
  useEffect(() => {
    if (skipNextUrlSync.current) {
      skipNextUrlSync.current = false;
      return;
    }
    const qs = buildQueryString({
      governingBodyId,
      ...filters,
      sortTokens,
      isAscending,
      page: pageState.page,
      limit: pageState.limit,
    });
    const nextSearch = qs ? `?${qs}` : "";
    if (nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }
  }, [
    governingBodyId,
    filters,
    sortTokens,
    isAscending,
    pageState,
    navigate,
    location.pathname,
    location.search,
  ]);

  const selectedGoverningBody = useMemo(
    () => governingBodies.find((gb) => gb.id === governingBodyId) || null,
    [governingBodies, governingBodyId],
  );

  const handleGoverningBodyChange = (nextId) => {
    setGoverningBodyId(nextId);
    setPageState((prev) => ({ ...prev, page: 0 }));
    setSelectedIds([]);
  };

  const handleFiltersChange = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPageState((prev) => ({ ...prev, page: 0 }));
    setSelectedIds([]);
  };

  const handleResetFilters = () => {
    setFilters({
      asOfDate: "",
      gender: "",
      eventHeight: null,
      ageGroup: "",
      divisionId: "",
      regionId: "",
      divisionMatch: TEXT_MATCH.EXACT,
      regionMatch: TEXT_MATCH.EXACT,
    });
    setPageState((prev) => ({ ...prev, page: 0 }));
    setSelectedIds([]);
  };

  const handleSortApply = ({ sortTokens: nextTokens, isAscending: nextAsc }) => {
    setSortState({ sortTokens: nextTokens, isAscending: nextAsc });
    setPageState((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = ({ page, limit }) => {
    setPageState({ page, limit });
  };

  const handleOpenCreate = () => {
    setEditDto(null);
    setCopyDto(null);
    setModalOpen(true);
  };

  const handleOpenCopy = (row) => {
    setEditDto(null);
    setCopyDto(row);
    setModalOpen(true);
  };

  const handleOpenEdit = (row) => {
    setCopyDto(null);
    setEditDto(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditDto(null);
    setCopyDto(null);
  };

  const handleSubmitForm = async (payload) => {
    try {
      // The backend expects governingBodyId from the form. For create we set it from the picker.
      const data = { ...payload, governingBodyId };
      await dispatch(saveQualifyingScore({ data })).unwrap();
      handleCloseModal();
      reloadList();
      dispatch(fetchQualifyingScoreFilterValues({ governingBodyId }));
    } catch (_) {
      // toast already shown in slice
    }
  };

  const handleDeleteFromForm = () => {
    setDeleteDto(editDto);
    setModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteDto?.id) return;
    try {
      await dispatch(deleteQualifyingScore({ id: deleteDto.id })).unwrap();
      setDeleteDto(null);
      setEditDto(null);
      reloadList();
    } catch (_) {
      // toast in slice
    }
  };

  const handleToggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const allRowIds = useMemo(() => list.rows.map((r) => r.id), [list.rows]);
  const allOnPageSelected =
    allRowIds.length > 0 && allRowIds.every((id) => selectedIds.includes(id));
  const someOnPageSelected =
    !allOnPageSelected && allRowIds.some((id) => selectedIds.includes(id));

  const handleToggleAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allRowIds.includes(id)));
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allRowIds.forEach((id) => next.add(id));
        return Array.from(next);
      });
    }
  };

  const selectedRows = useMemo(
    () => selectedIds.map((id) => knownRows[id]).filter(Boolean),
    [knownRows, selectedIds],
  );

  const handleSelectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const rows = await fetchAllMatchingQualifyingScores(apiParams, list.totalElements);
      rememberRows(rows);
      setSelectedIds(rows.map((r) => r.id));
    } catch (err) {
      toast.error(err?.message || "Failed to select all matching rows.");
    } finally {
      setSelectingAll(false);
    }
  };

  const handleBulkFinished = ({ done, failed }, savedRows) => {
    rememberRows(savedRows);
    if (done > 0) reloadList();
    if (failed === 0) setSelectedIds([]);
  };

  const handleCloseBulk = () => {
    if (bulkRunning) return;
    setBulkOpen(false);
  };

  const handleOpenClone = () => {
    if (selectedIds.length === 0) {
      toast.info("Select at least one row to copy.");
      return;
    }
    setCloneOpen(true);
  };

  const handleCloneSubmit = async ({ targetFromDate, targetToDate, overwriteExisting }) => {
    if (!governingBodyId) return;
    try {
      const response = await dispatch(
        cloneQualifyingScores({
          data: {
            governingBodyId,
            sourceIds: selectedIds,
            targetFromDate,
            targetToDate,
            overwriteExisting,
          },
        }),
      ).unwrap();
      const copied = response?.copied ?? 0;
      const skipped = response?.skipped ?? 0;
      const skippedIds = response?.skippedSourceIds || [];
      if (skipped > 0) {
        toast.warn(
          `Copied ${copied}, skipped ${skipped} (existing rows at target dates). ` +
            "Re-open the dialog with Overwrite checked to replace them.",
          { autoClose: 8000 },
        );
      } else {
        toast.success(`Copied ${copied} qualifying score${copied === 1 ? "" : "s"}.`);
      }
      setCloneOpen(false);
      setSelectedIds([]);
      reloadList();
      return { copied, skipped, skippedIds };
    } catch (_) {
      // toast in slice
      return null;
    }
  };

  const handleExport = async (format) => {
    if (!governingBodyId) return;
    try {
      await downloadQualifyingScoresExport({
        format,
        params: {
          governingBodyId,
          asOfDate: filters.asOfDate || null,
          ageGroup: filters.ageGroup || null,
          gender: filters.gender || null,
          eventHeight: filters.eventHeight || null,
          divisionId: filters.divisionId || null,
          regionId: filters.regionId || null,
          divisionMatch: matchParam(filters.divisionId, filters.divisionMatch),
          regionMatch: matchParam(filters.regionId, filters.regionMatch),
          sort: sortTokens.join(","),
          isAscending,
        },
      });
    } catch (err) {
      toast.error(err?.message || "Failed to download export.");
    }
  };

  // Division/region suggestions: every value in use for this governing body (filter-values),
  // plus any on the loaded page in case that call failed.
  const mergeOptions = (serverValues, key) => {
    const seen = new Map();
    [...(serverValues || []), ...list.rows.map((r) => r[key])]
      .filter((v) => v != null && String(v).trim() !== "")
      .forEach((v) => {
        const trimmed = String(v).trim();
        const lower = trimmed.toLowerCase();
        if (!seen.has(lower)) seen.set(lower, trimmed);
      });
    return [...seen.values()].sort((x, y) =>
      x.localeCompare(y, undefined, { sensitivity: "base", numeric: true }),
    );
  };
  const divisionOptions = useMemo(
    () => mergeOptions(filterValues?.divisions, "divisionId"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues, list.rows],
  );
  const regionOptions = useMemo(
    () => mergeOptions(filterValues?.regions, "regionId"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterValues, list.rows],
  );

  const renderTable = () => {
    if (loading) {
      return (
        <Box className={classes.progressWrap}>
          <CircularProgress />
        </Box>
      );
    }
    if (!governingBodyId) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="body1">
            Pick a governing body to see qualifying scores.
          </Typography>
        </Box>
      );
    }
    if (list.rows.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <Typography variant="body1">
            No qualifying scores match these filters.
          </Typography>
          <Box style={{ marginTop: 12 }}>
            <Button variant="text" onClick={handleResetFilters}>
              Reset filters
            </Button>
            <Button variant="contained" color="primary" onClick={handleOpenCreate}>
              + Create
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} className={classes.tableWrapper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell} padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={someOnPageSelected}
                  checked={allOnPageSelected}
                  onChange={handleToggleAllOnPage}
                  inputProps={{ "aria-label": "Select all rows on page" }}
                />
              </TableCell>
              <TableCell className={classes.headerCell}>Age</TableCell>
              <TableCell className={classes.headerCell}>Height</TableCell>
              <TableCell className={classes.headerCell}>Gender</TableCell>
              <TableCell className={classes.headerCell}># dives</TableCell>
              <TableCell className={classes.headerCell}>Points</TableCell>
              <TableCell className={classes.headerCell}>Min DD</TableCell>
              <TableCell className={classes.headerCell}>Valid</TableCell>
              <TableCell className={classes.headerCell}>Division</TableCell>
              <TableCell className={classes.headerCell}>Region</TableCell>
              <TableCell className={classes.headerCell}>State</TableCell>
              <TableCell className={classes.headerCell}>Template</TableCell>
              <TableCell className={classes.headerCell}>Updated</TableCell>
              <TableCell
                className={`${classes.headerCell} ${classes.stickyActionHeader}`}
              >
                <span className="sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.rows.map((row, idx) => {
              const isChecked = selectedIds.includes(row.id);
              return (
                <TableRow
                  key={row.id}
                  className={idx % 2 ? classes.rowOdd : classes.rowEven}
                  hover
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={() => handleToggleRow(row.id)}
                      inputProps={{ "aria-label": `Select row ${row.id}` }}
                    />
                  </TableCell>
                  <TableCell className={classes.cell}>{row.ageGroup ?? dash}</TableCell>
                  <TableCell className={classes.cell}>{heightLabel(row.eventHeight)}</TableCell>
                  <TableCell className={classes.cell}>{row.gender ?? dash}</TableCell>
                  <TableCell className={classes.cell}>{row.diveCount ?? dash}</TableCell>
                  <TableCell className={classes.cell}>{formatPoints(row.points)}</TableCell>
                  <TableCell className={classes.cell}>
                    {row.minimumDd != null ? row.minimumDd : dash}
                  </TableCell>
                  <TableCell
                    className={classes.cell}
                    title={`${row.fromDate ?? "?"} to ${row.toDate ?? "?"}`}
                  >
                    {formatDateRange(row.fromDate, row.toDate) ?? dash}
                  </TableCell>
                  <TableCell className={classes.cell}>{row.divisionId ?? dash}</TableCell>
                  <TableCell className={classes.cell}>{row.regionId ?? dash}</TableCell>
                  <TableCell className={classes.cell}>{row.state ?? dash}</TableCell>
                  <TableCell className={classes.cell}>
                    {row.messageTemplateDescription ?? dash}
                    {row.messageTemplateActive === false && (
                      <span style={{ color: "#b42318" }}> (inactive)</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={classes.cell}
                    title={[
                      formatFullDateTime(row.updatedAt),
                      row.updatedBy != null ? `by user ${row.updatedBy}` : null,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {formatShortDateTime(row.updatedAt) ?? dash}
                  </TableCell>
                  <TableCell className={classes.stickyAction}>
                    <Tooltip title="Edit">
                      <IconButton
                        className={classes.iconButton}
                        color="primary"
                        onClick={() => handleOpenEdit(row)}
                        aria-label={`Edit qualifying score ${row.id}`}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy into a new score">
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => handleOpenCopy(row)}
                        aria-label={`Copy qualifying score ${row.id} into a new score`}
                      >
                        <FileCopyOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        className={`${classes.iconButton} ${classes.deleteIcon}`}
                        onClick={() => setDeleteDto(row)}
                        aria-label={`Delete qualifying score ${row.id}`}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <ListPagination
          filters={{
            page: list.currentPage,
            limit: pageState.limit,
            total: list.totalElements,
          }}
          fetchFunction={handlePageChange}
        />
      </TableContainer>
    );
  };

  return (
    <Box className={classes.page}>
      <Container data-container="container">
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {PAGES_TITLES.QUALIFYING_SCORES}
          </Typography>
          <Typography className={classes.subtitle}>
            Manage qualifying scores for each governing body.
          </Typography>
        </Box>

        <QualifyingScoreFilters
          governingBodies={governingBodies}
          governingBodyId={governingBodyId}
          governingBodiesLoading={governingBodiesLoading}
          filters={filters}
          sortTokens={sortTokens}
          isAscending={isAscending}
          divisionOptions={divisionOptions}
          regionOptions={regionOptions}
          onGoverningBodyChange={handleGoverningBodyChange}
          onFiltersChange={handleFiltersChange}
          onSortChange={handleSortApply}
          onReset={handleResetFilters}
          onCreate={handleOpenCreate}
          canCreate={Boolean(governingBodyId)}
          onExportCsv={() => handleExport("csv")}
          onExportXlsx={() => handleExport("xlsx")}
          canExport={Boolean(governingBodyId) && list.rows.length > 0}
        />

        {selectedIds.length > 0 && (
          <Box className={classes.selectionBar}>
            <Typography variant="body2">
              {selectedIds.length} of {list.totalElements} row
              {list.totalElements === 1 ? "" : "s"} selected
            </Typography>
            <Box>
              {selectedIds.length < list.totalElements && (
                <Button
                  size="small"
                  color="primary"
                  onClick={handleSelectAllMatching}
                  disabled={selectingAll}
                  style={{ marginRight: 8 }}
                >
                  {selectingAll ? "Selecting…" : `Select all ${list.totalElements} matching`}
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => setBulkOpen(true)}
                style={{ marginRight: 8 }}
              >
                Set message template…
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleOpenClone}
              >
                Copy to new dates…
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedIds([])}
                style={{ marginLeft: 8 }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        )}

        {renderTable()}
      </Container>

      {modalOpen && governingBodyId && (
        <ConfirmDialog
          open
          handleClose={handleCloseModal}
          title={
            editDto?.id
              ? TITLES.EDIT_QUALIFYING_SCORE
              : copyDto
              ? "Copy qualifying score"
              : TITLES.CREATE_QUALIFYING_SCORE
          }
        >
          <QualifyingScoreForm
            selectedDto={editDto || {}}
            copyFrom={copyDto}
            governingBodyName={
              selectedGoverningBody?.name ||
              selectedGoverningBody?.shortName ||
              selectedGoverningBody?.abbreviation ||
              ""
            }
            governingBodyId={governingBodyId}
            messageTemplates={messageTemplates}
            isSubmitting={saving}
            onSubmit={handleSubmitForm}
            onCancel={handleCloseModal}
            onDelete={editDto?.id ? handleDeleteFromForm : undefined}
          />
        </ConfirmDialog>
      )}

      {cloneOpen && (
        <ConfirmDialog
          open
          handleClose={() => setCloneOpen(false)}
          title={TITLES.COPY_QUALIFYING_SCORES}
        >
          <QualifyingScoreCloneDialog
            open
            selectedRows={selectedRows}
            governingBodyName={
              selectedGoverningBody?.name ||
              selectedGoverningBody?.abbreviation ||
              ""
            }
            isSubmitting={saving}
            onCancel={() => setCloneOpen(false)}
            onSubmit={handleCloneSubmit}
          />
        </ConfirmDialog>
      )}

      {bulkOpen && (
        <ConfirmDialog
          open
          handleClose={handleCloseBulk}
          disableBackdropClose
          title={TITLES.BULK_TEMPLATE_QUALIFYING_SCORES}
        >
          <QualifyingScoreBulkTemplateDialog
            ids={selectedIds}
            rowsById={knownRows}
            templates={messageTemplates}
            onRunningChange={setBulkRunning}
            onFinished={handleBulkFinished}
            onClose={handleCloseBulk}
          />
        </ConfirmDialog>
      )}

      {deleteDto && (
        <ConfirmDialog
          open
          handleClose={() => setDeleteDto(null)}
          title={TITLES.DELETE_MODAL}
        >
          <Box>
            <Typography variant="body1" className={classes.deleteSummary}>
              Delete qualifying score for{" "}
              <b>
                {deleteDto.ageGroup}, {deleteDto.gender}, {heightLabel(deleteDto.eventHeight)},{" "}
                {deleteDto.diveCount} dives
              </b>{" "}
              valid {deleteDto.fromDate} → {deleteDto.toDate}?
            </Typography>
            {Array.isArray(deleteDto.emailRecipients) &&
              deleteDto.emailRecipients.length > 0 && (
                <Typography variant="caption" style={{ color: "#667085" }}>
                  This will also remove {deleteDto.emailRecipients.length} recipient
                  {deleteDto.emailRecipients.length === 1 ? "" : "s"}.
                </Typography>
              )}
            <Box className={classes.modalActions}>
              <Button onClick={() => setDeleteDto(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleConfirmDelete}
                disabled={saving}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </ConfirmDialog>
      )}
    </Box>
  );
};

export default QualifyingScores;
